import EmailService from '@/lib/email';
import { logger } from '@/lib/logger';

/**
 * Returns true if the role corresponds to any clinical or administrative staff role.
 * Explicitly excludes Patients so they remain completely unaffected.
 */
export function isStaffRole(role?: string | null): boolean {
  if (!role) return true; // Portal accounts are staff by default unless explicitly designated as patient
  const clean = role.trim().toLowerCase();
  // Exclude Patients explicitly
  if (clean === 'patient') return false;
  // All other clinical and non-clinical roles are staff
  // (e.g. Doctor, Nurse, Admin, SuperAdmin, BranchAdmin, Staff, Technician,
  // clinical_staff, Clinical_reviewer, CareCoordinator, Nutritionist, email_Manager, SMS_Manager)
  return true;
}

/**
 * Generates a cryptographically strong 6-digit numeric OTP code.
 */
export function generateMfaOtp(): string {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const code = (array[0] % 900000) + 100000;
    return code.toString();
  }
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Masks an email for privacy display (e.g. j***n@monitorhealth.ai)
 */
export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  if (local.length <= 2) {
    return `${local[0]}*@${domain}`;
  }
  const first = local[0];
  const last = local[local.length - 1];
  const stars = '*'.repeat(Math.min(local.length - 2, 4));
  return `${first}${stars}${last}@${domain}`;
}

export interface MfaEmailParams {
  to: string;
  name?: string;
  otpCode: string;
  expirationMinutes?: number;
}

/**
 * Builds an HTML email for MFA verification.
 */
export function buildMfaEmailHtml(params: { name?: string; otpCode: string; expirationMinutes?: number }): string {
  const { name, otpCode, expirationMinutes = 10 } = params;
  const recipientGreeting = name ? `Dear <strong>${name}</strong>,` : 'Hello,';

  return `
<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>Your MonitorHealth.AI Security Verification Code</title>
  <style>
    body, table, td, p, a {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f7f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f4f7f6; padding: 36px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" style="max-width: 560px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #e2e8f0;">
          
          <!-- Header -->
          <tr>
            <td style="background-color: #1a7a4a; background: linear-gradient(135deg, #1a7a4a 0%, #145d39 100%); padding: 30px 36px; text-align: left;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <div style="font-size: 22px; font-weight: 800; color: #ffffff; letter-spacing: -0.5px;">
                      MonitorHealth<span style="color: #6ee7b7;">.AI</span>
                    </div>
                    <div style="margin-top: 4px; color: #d1fae5; font-size: 13px; font-weight: 500;">
                      Clinical Network Staff Authentication
                    </div>
                  </td>
                  <td align="right" valign="top">
                    <span style="display: inline-block; background-color: #238652; color: #ffffff; font-size: 11px; font-weight: 700; padding: 5px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid rgba(255,255,255,0.3);">
                      🔒 2FA Security
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Main Body -->
          <tr>
            <td style="padding: 36px 36px 28px 36px;">
              <h2 style="margin: 0 0 12px 0; color: #0f172a; font-size: 20px; font-weight: 700; line-height: 1.3;">
                Two-Factor Authentication Code
              </h2>
              <p style="margin: 0 0 16px 0; color: #475569; font-size: 14px; line-height: 1.6;">
                ${recipientGreeting}
              </p>
              <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.6;">
                A sign-in request was initiated for your MonitorHealth.AI clinical staff account. Please use the following 6-digit one-time code to complete your login:
              </p>

              <!-- OTP Code Display Box -->
              <div style="text-align: center; margin: 28px 0; padding: 24px 16px; background-color: #f8fafc; border: 2px dashed #cbd5e1; border-radius: 12px;">
                <div style="display: inline-block; letter-spacing: 10px; font-family: 'Courier New', Courier, monospace; font-size: 34px; font-weight: 800; color: #166534; padding-left: 10px;">
                  ${otpCode}
                </div>
                <p style="margin: 12px 0 0 0; font-size: 12px; color: #64748b; font-weight: 500;">
                  ⏱️ Valid for <strong>${expirationMinutes} minutes</strong> &bull; Single-use only
                </p>
              </div>

              <!-- Security Warning -->
              <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; border-radius: 6px; padding: 12px 16px; margin: 24px 0 16px 0;">
                <p style="margin: 0; font-size: 12px; color: #92400e; line-height: 1.5;">
                  <strong>Important Security Notice:</strong> Never share this code with anyone. MonitorHealth.AI staff will never ask for your verification code. If you did not initiate this login, your credentials may be compromised—please reset your password immediately.
                </p>
              </div>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8fafc; padding: 20px 36px; border-top: 1px solid #e2e8f0; text-align: center;">
              <p style="margin: 0 0 6px 0; font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;">
                HIPAA &amp; HITECH Compliant Security Protection
              </p>
              <p style="margin: 0; font-size: 11px; color: #94a3b8; line-height: 1.5;">
                This automated security notification is delivered in accordance with HIPAA § 164.312(a)(2)(iv) Multi-Factor Authentication standards for clinical personnel.
              </p>
              <p style="margin: 10px 0 0 0; font-size: 11px; color: #94a3b8;">
                &copy; ${new Date().getFullYear()} MonitorHealth.AI &bull; All rights reserved.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Sends the MFA OTP email to the staff user.
 */
export async function sendMfaEmail(params: MfaEmailParams): Promise<{ success: boolean; error?: string }> {
  try {
    const { to, name, otpCode, expirationMinutes = 10 } = params;
    logger.info('[MFA] Dispatching MFA email OTP', { to: maskEmail(to) });

    const htmlBody = buildMfaEmailHtml({ name, otpCode, expirationMinutes });
    const textBody = `Your MonitorHealth.AI verification code is ${otpCode}. It expires in ${expirationMinutes} minutes. Do not share this code with anyone.`;

    const result = await EmailService.sendEmail({
      to,
      subject: `Your MonitorHealth.AI Security Verification Code: ${otpCode}`,
      body: htmlBody,
      textBody,
    });

    if (result.error) {
      logger.error('[MFA] Failed to send MFA email:', result.error);
      return { success: false, error: result.error?.message || 'Failed to send verification email' };
    }

    return { success: true };
  } catch (err: any) {
    logger.error('[MFA] Unexpected error sending MFA email:', err);
    return { success: false, error: err?.message || 'Failed to send verification email' };
  }
}
